import { createFileRoute, Link } from "@tanstack/react-router";
import ogHomeImg from "@/assets/og/og-home.jpg";
import { useT } from "@/i18n/LanguageProvider";
import { hreflangLinks, breadcrumbScript, ogImageMeta, SITE_URL } from "@/lib/seo";
import { jsonLdScript } from "@/lib/json-ld";
import { LegalPage } from "@/components/legal/LegalPage";
import { GrievanceContact } from "@/components/legal/GrievanceContact";
import { getAccessibilityStatement } from "@/lib/a11y-statement.functions";
import { statementBlocks } from "@/lib/a11y-statement";

const URL = "https://jantrobazar.com/accessibility";

export const Route = createFileRoute("/accessibility")({
  head: () => ({
    meta: [
      { title: "Accessibility Statement — Jantro Bazar" },
      {
        name: "description",
        content:
          "How Jantro Bazar works to meet WCAG 2.1 AA — keyboard navigation, screen-reader support, colour contrast, bilingual UI, known limitations, and how to report an accessibility barrier.",
      },
      { property: "og:title", content: "Accessibility Statement — Jantro Bazar" },
      {
        property: "og:description",
        content:
          "Our commitment to an accessible parts marketplace for every customer in Bangladesh.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Jantro Bazar" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
      ...ogImageMeta(`${SITE_URL}${ogHomeImg}`, "Accessibility Statement — Jantro Bazar"),
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(URL)],
    scripts: [
      breadcrumbScript("Accessibility", URL),
      jsonLdScript({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Accessibility Statement",
        url: URL,
        inLanguage: ["en", "bn"],
        isPartOf: { "@type": "WebSite", name: "Jantro Bazar", url: "https://jantrobazar.com/" },
      }),
    ],
  }),
  loader: () => getAccessibilityStatement(),
  errorComponent: () => (
    <div className="container-app py-16 text-sm text-muted-foreground">
      Accessibility statement is temporarily unavailable. Please try again.
    </div>
  ),
  notFoundComponent: () => (
    <div className="container-app py-16 text-sm text-muted-foreground">Page not found.</div>
  ),
  component: AccessibilityPage,
});

function AccessibilityPage() {
  const { lang } = useT();
  const en = lang === "en";
  const { doc } = Route.useLoaderData();
  return (
    <LegalPage
      title={en ? "Accessibility Statement" : "অ্যাক্সেসিবিলিটি স্টেটমেন্ট"}
      subtitle={
        en
          ? `Last updated: ${doc.updatedEn}. ${doc.introEn}`
          : `সর্বশেষ আপডেট: ${doc.updatedBn}। ${doc.introBn}`
      }
      updated={en ? doc.updatedEn : doc.updatedBn}
    >
      {doc.sections.map((s, i) => (
        <Section key={s.id + i} title={en ? s.titleEn : s.titleBn}>
          {statementBlocks(en ? s.bodyEn : s.bodyBn).map((b, k) =>
            b.kind === "p" ? (
              <p key={k}>{b.text}</p>
            ) : (
              <ul key={k} className="list-disc pl-5 space-y-1.5">
                {b.items.map((it, m) => (
                  <li key={m}>{it}</li>
                ))}
              </ul>
            ),
          )}
        </Section>
      ))}

      <div className="mt-4 flex flex-wrap gap-3">
        <a
          href="https://wa.me/8801811600591"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90"
        >
          💬 WhatsApp 8801811600591
        </a>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          {en ? "Contact us" : "যোগাযোগ"}
        </Link>
      </div>
      <GrievanceContact />
    </LegalPage>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-bold text-primary mb-3">{title}</h2>
      <div className="text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div>
    </section>
  );
}
